import { FunctionComponent, useEffect, useState, useRef } from "react";
import {
  PageTitle,
  OrderTable,
  Modal,
  ClientInformations,
  CommentaryModal,
  PointOfSaleModalStock,
} from "_components";
import { useTranslation } from "react-i18next";
import { useLoading, useModal } from "_hooks";
import {
  OrderResponseType,
  Role,
  exportOrdersExcelData,
  getOrders,
  InfoClient,
  updateOrder,
  updateOrderCommentary,
  getOrdersOne,
  exportOrdersCsvData,
  point_of_sale,
  updatePointOfSaleOrders,
  ExportOrdersField,
} from "_api";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { atomDealers, atomUserRole, atomUserRoles } from "_atoms";
import { sortByRGD2Date } from "_utils/utils";
import "./order-page.scss";
import Select, { components, InputProps, GroupBase, StylesConfig } from 'react-select';

const customStyles: StylesConfig = {
  menu: (provided) => ({
    ...provided,
    zIndex: 9999, // a high value to ensure it's on top
  }),
  // optionally, you can style other parts if needed
};

const OrdersPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const { setModal } = useModal();

  const FILTER_COLUMNS = [
    { value: "orderNumber", label: t("order_number") },
    { value: "year", label: t("year") },
    { value: "status", label: t("status") },
    { value: "dealerName", label: t("dealer_name") },
    { value: "brand", label: t("brand") },
    { value: "model", label: t("model") },
    { value: "infoClient", label: t("info_client") },
    { value: "productionDate", label: t("production_date") },
  ];

  const [orders, setOrders] = useState<OrderResponseType[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderResponseType[]>([]);
  const [activeFilters, setActiveFilters] = useState<
    Array<{ column?: string; value: string }>
  >([]);
  const [selectedColumn, setSelectedColumn] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  const { setLoading } = useLoading();
  const [isClientInfoModal, SetIsClientInfoModal] = useState(false);
  const [userRole] = useRecoilState(atomUserRole);
  const [userRoles] = useRecoilState(atomUserRoles);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [currentClient, setCurrentClient] = useState<InfoClient | null>(null);
  const [isCommentaryModal, setIsCommentaryModal] = useState(false);
  const [currentCommentary, setCurrentCommentary] =
    useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<Array<{ label: string; value: string }>>([]);
  const isInfoClientColumn = selectedColumn === "infoClient";
  const isProductionDateColumn = selectedColumn === "productionDate";
  const [selectedOption, setSelectedOption] = useState<{ label: string; value: string } | null>(null);
  const CustomSelectInput: React.FC<InputProps<{ label: string; value: string }, false, GroupBase<{ label: string; value: string }>>> = (props) => {
    const { onKeyDown } = props;

    return (
      <components.Input
        {...props}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleAddFilter(); // your function to add filter
          }
          if (onKeyDown) {
            onKeyDown(e);
          }
        }}
      />
    );
  };

const [isOrdersToReceive, setIsOrdersToReceive] = useState(false);
const applySpecialFilter = (newToggleState?: boolean) => {
  const shouldApplyFilter = newToggleState !== undefined ? newToggleState : isOrdersToReceive;

  if (shouldApplyFilter) {
    const hasActiveFilter = activeFilters.some(
      filter => filter.column === "status" && filter.value === "active/ouverte"
    );
    if (!hasActiveFilter) {
      setActiveFilters([
        ...activeFilters,
        { column: "status", value: "active/ouverte" }
      ]);
    }
  } else {
    const newFilters = activeFilters.filter(
      filter => !(filter.column === "status" && filter.value === "active/ouverte")
    );
    setActiveFilters(newFilters);
  }
};

const ToggleSwitch = ({ isOn, handleToggle }: { isOn: boolean; handleToggle: () => void }) => {
  return (
    <label className="toggle-switch">
      <input type="checkbox" checked={isOn} onChange={handleToggle} />
      <span className="slider round"></span>
    </label>
  );
};

  function canToggle(userRole: Role | null): boolean {
    return (
      !!userRole &&
      (userRole.name === "Manager_commercial" ||
        userRole.name === "ADV" ||
        userRole.name === "Manager_ADV" ||
        userRole.name === "Dealer")
    );
  }

  const [userCanToggle] = useState<boolean>(canToggle(userRole));
  const [dealers] = useRecoilState(atomDealers);
  const urlParams = new URLSearchParams(window.location.search);
  const orderId = urlParams.get("order");

  function initData(): void {
    setLoading(true);
    if (orderId) {
      const params = {
        criterias: {
          dealerId: dealers,
        },
      };
      getOrdersOne(params, orderId)
        .then((ordersResult) => {
          const ordersData = ordersResult.data.content;
          setOrders(ordersData);
          setFilteredOrders(ordersData);
          setLoading(false);
          setNoDataFound(ordersData.length === 0);
        })
        .catch((error) => {
          setLoading(false);
          if (error.response) {
            switch (error.response.status) {
              case 412:
                toast.error(t("toast_get_metadata_error_pim_orders"));
                break;
              case 413:
                toast.error(t("toast_get_data_error_pim_orders"));
                break;
              default:
                toast.error(t("toast_get_data_error"));
            }
          } else {
            toast.error(t("toast_get_data_error"));
          }
        });
    } else {
      const params = {
        criterias: {
          dealerId: dealers,
        },
      };
      getOrders(params)
        .then((ordersResult) => {
          const ordersData = ordersResult.data.content;
          setOrders(ordersData);
          setFilteredOrders(ordersData);
          setLoading(false);
          setNoDataFound(ordersData.length === 0);
        })
        .catch((error) => {
          setLoading(false);
          if (error.response) {
            switch (error.response.status) {
              case 412:
                toast.error(t("toast_get_metadata_error_pim_orders"));
                break;
              case 413:
                toast.error(t("toast_get_data_error_pim_orders"));
                break;
              default:
                toast.error(t("toast_get_data_error"));
            }
          } else {
            toast.error(t("toast_get_data_error"));
          }
        });
    }
  }

// A function to get unique options for a column
const loadOptionsForColumn = (column: string) => {
  const optionsSet = new Set<string>();
  orders.forEach(order => {
    const value = order[column as keyof OrderResponseType];
    if (value) {
      optionsSet.add(String(value));
    }
  });
  const optionsArray = Array.from(optionsSet).map((val) => ({ label: val, value: val }));
    if (column === "orderNumber" || column === "year") {
      optionsArray.sort((a, b) => Number(b.value) - Number(a.value)); // Descending
    } else {
      optionsArray.sort((a, b) => a.label.localeCompare(b.label)); // Ascending
    }
  setFilterOptions(optionsArray);
};

useEffect(() => {
  applyFilters();
}, [activeFilters]);
// When user selects a filter column
const handleSelectFilterColumn = (columnValue: string) => {
  setSelectedColumn(columnValue);

  setSearchTerm(""); 
  setSelectedOption(null);

  if (columnValue === "infoClient") {
    setFilterOptions([]); // Clear options for infoClient
  } else {
    loadOptionsForColumn(columnValue);
  }
};

// When user selects an option
const handleOptionChange = (option: { label: string; value: string } | null) => {
  setSelectedOption(option);
};

const handleAddFilter = () => {
  if (!selectedColumn) return;

  // On vérifie si un filtre existe déjà pour cette colonne
  const isReplacing = activeFilters.some(f => f.column === selectedColumn);

  // On bloque seulement si on AJOUTE un nouveau type de filtre ET qu'on dépasse la limite
  if (!isReplacing && activeFilters.length >= 5) {
    toast.error(t("maximum_filters_reached"));
    return;
  }

  // 1. On retire l'ancien filtre de cette colonne s'il existe
  const otherFilters = activeFilters.filter(
    (filter) => filter.column !== selectedColumn
  );

  // 2. On prépare le nouveau
  let newFilter = null;

  if (selectedColumn === "infoClient") {
    if (searchTerm.trim() !== "") {
      newFilter = { column: "infoClient", value: searchTerm.trim() };
    }
  } else if (selectedColumn === "productionDate") {
    if (searchTerm.trim() !== "") {
      newFilter = { column: "productionDate", value: searchTerm.trim() };
    }
  } else {
    // Cas du Select
    if (selectedOption) {
      newFilter = { column: selectedColumn, value: selectedOption.value };
      if (selectedColumn === "status") {
        if (selectedOption.value === "active/ouverte") {
          setIsOrdersToReceive(true);
        } else {
          // Si on choisit "facturée" (ou autre), on FORCE le toggle à s'éteindre
          setIsOrdersToReceive(false); 
        }
      }
    }
  }

  // 3. Mise à jour de l'état
  if (newFilter) {
    setActiveFilters([...otherFilters, newFilter]);
    setSelectedOption(null);
    setSearchTerm("");
  }
};

// Render
<Select
  options={filterOptions}
  onChange={handleOptionChange}
  placeholder={t("select_option")}
  isClearable
/>


  const applyFilters = () => {
    if (activeFilters.length === 0) {
      setFilteredOrders(orders);
      setNoDataFound(orders.length === 0);
      return;
    }

    let result = [...orders];

    activeFilters.forEach((filter) => {
      if (filter.column) {
        if (filter.column === "infoClient") {
          result = result.filter((order) => {
            const fullName =
              `${order.infoClient.firstName} ${order.infoClient.lastName}`.toLowerCase();
            return fullName.includes(filter.value.toLowerCase());
          });
        } else if (filter.column === "productionDate") {
          result = result.filter((order) => {
              const orderDate = new Date(order.productionDate);
              const filterDate = new Date(filter.value);
              return (
                orderDate.getFullYear() === filterDate.getFullYear() &&
                orderDate.getMonth() === filterDate.getMonth() &&
                orderDate.getDate() === filterDate.getDate()
              );
            });
        } else {
          result = result.filter((order) => {
            const value = order[filter.column as keyof OrderResponseType];
            return (
              value &&
              String(value).toLowerCase().includes(filter.value.toLowerCase())
            );
          });
        }
      } else {
        result = result.filter((order) => {
          return Object.values(order).some(
            (value) =>
              value &&
              String(value).toLowerCase().includes(filter.value.toLowerCase())
          );
        });
      }
    });

    setFilteredOrders(result);
    setNoDataFound(result.length === 0);
  };

  useEffect(() => {
    if (isOrdersToReceive) {
        applySpecialFilter();
     } else {
        applyFilters();
    }
  }, [activeFilters, orders, isOrdersToReceive]);


  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
  
    // Définition de la colonne ciblée (ou undefined si recherche globale)
    const currentColumn = selectedColumn || undefined;
    
    const isReplacing = activeFilters.some(f => f.column === currentColumn);
  
    if (!isReplacing && activeFilters.length >= 5) {
      toast.error(t("maximum_filters_reached"));
      return;
    }
  
    // 1. On retire l'ancien
    const otherFilters = activeFilters.filter(
      (filter) => filter.column !== currentColumn
    );
  
    // 2. On ajoute le nouveau
    setActiveFilters([
      ...otherFilters,
      {
        column: currentColumn,
        value: searchTerm.trim(),
      },
    ]);
  
    setSearchTerm("");
  };


  const clearAllFilters = () => {
    setIsOrdersToReceive(false);
    setActiveFilters([]);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch(e as unknown as React.FormEvent);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

function createExportParams() {
  const groupedFilters = activeFilters.reduce((acc, filter) => {
    const column = filter.column;
    if (column === undefined || column === "infoClient") return acc;

    if (!acc[column]) acc[column] = [];
    acc[column].push(filter.value);
    return acc;
  }, {} as Record<string, string[]>);

  const criterias: any = {};

  Object.entries(groupedFilters).forEach(([key, values]) => {
    switch (key) {
      case "orderNumber":
        criterias.commandeId = Array.from(new Set(values));
        break;
      case "year":
        criterias.year = Array.from(new Set(values));
        break;
      case "status":
        criterias.status = Array.from(new Set(values));
        break;
      case "dealerName":
        criterias.dealerName = Array.from(new Set(values));
        break;
      case "brand":
        criterias.brand = Array.from(new Set(values));
        break;
      case "model":
        criterias.model = Array.from(new Set(values));
        break;
      case "productionDate":
        criterias.productionDate = Array.from(new Set(values));
        break;
      default:
        break;
    }
  });

  // Handle infoClientSearchTerm separately
  const infoClientFilters = activeFilters.filter(f => f.column === "infoClient");
  const infoClientSearchTerm = infoClientFilters.length > 0
    ? infoClientFilters.map(f => f.value).join(" ")
    : "";

  return {
    inclusive: true,
    infoClientSearchTerm: infoClientSearchTerm || "...",
    //...(infoClientSearchTerm && { infoClientSearchTerm }), // Only include if non-empty
    criterias: {
      dealerId: dealers,
      ...criterias,
    },
    language: userRoles?.language,
  };
}

  // Generic export function
  async function handleExport(
    exportFn: (params: any) => Promise<any>,
    downloadHandler: (result: any) => void
  ): Promise<void> {
    setLoading(true);
    const params = createExportParams();

    try {
      const result = await exportFn(params);
      downloadHandler(result);
    } catch (error: any) {
      if (error.response && error.response.status === 412) {
        toast.error(t("toast_get_data_error_excel"));
        console.error(t("toast_get_data_error_excel"), error);
      } else {
        console.log("error: ", error);
      }
    } finally {
      setLoading(false);
    }
  }

  // Simplified export functions
  function exportCsvData(): void {
    handleExport(exportOrdersCsvData, (result) => {
      const anchor: HTMLAnchorElement = document.createElement("a");
      anchor.href = "data:text/csv;charset=utf-8," + encodeURI(result.data);
      anchor.download = "export-" + new Date().toLocaleDateString() + ".csv";
      anchor.click();
    });
  }

  function exportExcelData(): void {
    handleExport(exportOrdersExcelData, (result) => {
      const data = new Blob([result.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(data);

      const anchor: HTMLAnchorElement = document.createElement("a");
      anchor.href = url;
      anchor.download = "export-" + new Date().toLocaleDateString() + ".xls";
      anchor.click();
    });
  }

  function handleOpenClientInfoModal(quote: OrderResponseType | null): void {
    if (quote !== null) {
      setModal(true);
      SetIsClientInfoModal(true);
      setCurrentOrderId(quote.orderNumber);
      setCurrentClient(quote.infoClient);
    } else {
      setModal(false);
      SetIsClientInfoModal(false);
      setCurrentClient(null);
    }
  }

  function handleOpenCommentaryModal(order: OrderResponseType | null): void {
    if (order !== null) {
      setModal(true);
      setIsCommentaryModal(true);
      setCurrentOrderId(order.orderNumber);
      setCurrentCommentary(order.infoDealer);
    } else {
      setModal(false);
      setIsCommentaryModal(false);
      setCurrentCommentary(null);
    }
  }

  const [selectedCUNO, setSelectedCUNO] = useState<string>("");
  const [isPointOfSaleModal, setIsPointOfSaleModal] = useState(false);
  const [currentPointOfSaleId, setCurrentPointOfSaleId] =
    useState<string | null>(null);

  const handleSelectedCUNOChange = (newCUNO: string) => {
    setSelectedCUNO(newCUNO);
  };

  const handleOpenPointOfSaleModal = (order: OrderResponseType | null) => {
    if (order !== null) {
      setModal(true);
      setIsPointOfSaleModal(true);
      setCurrentPointOfSaleId(order.orderNumber);
    } else {
      setModal(false);
      setIsPointOfSaleModal(false);
      setCurrentPointOfSaleId(null);
    }
  };

  function handleClientInfoChange(infoClient: InfoClient): void {
    setLoading(true);
    if (infoClient) {
      const order = orders.find(
        (quote) => quote.orderNumber === currentOrderId
      );
      if (order) {
        updateOrder(order.orderNumber, infoClient).then(
          () => {
            const updatedOrders = orders.map((order) => {
              if (order.orderNumber === currentOrderId) {
                order.infoClient = infoClient;
              }
              return order;
            });
            setOrders(updatedOrders);
            applyFilters();
            setLoading(false);
          },
          () => {
            setLoading(false);
            toast.error(t("toast_get_data_error"));
          }
        );
      }
    }
  }

  function handleOrderCommentaryChange(commentary: string): void {
    setLoading(true);
    if (commentary) {
      // 1. requete.
      const order = orders.find(
        (order) => order.orderNumber === currentOrderId
      );
      if (order) {
        updateOrderCommentary(order.orderNumber, commentary).then(
          () => {
            const updatedOrders = orders.map((order) => {
              if (order.orderNumber === currentOrderId) {
                order.infoDealer = commentary;
              }
              return order;
            });
            setOrders(updatedOrders);
            applyFilters();
            toast.success(t("order_update_commentary"));
            setLoading(false);
          },
          () => {
            setLoading(false);
            toast.error(t("toast_get_data_error"));
          }
        );
      }
    }
  }

  const handleSelectedOptionChange = (
    point_of_sale: point_of_sale | undefined
  ) => {
    setLoading(true);
    if (point_of_sale) {
      const filteredOrders = orders.find(
        (order: OrderResponseType) => order.orderNumber === currentPointOfSaleId
      );
      const selectedORNO = filteredOrders?.orderNumber;
      if (filteredOrders) {
        updatePointOfSaleOrders(selectedORNO, point_of_sale)
          .then(() => {
            initData();
            toast.success(t("point_of_sale_update_success"));
            setLoading(false);
          })
          .catch((error: { response: { status: any } }) => {
            setLoading(false);
            if (error.response) {
              switch (error.response.status) {
                case 412:
                  toast.error(t("toast_error_updating_point_of_sale_on_azure"));
                  break;
                case 413:
                  toast.error(t("toast_error_updating_point_of_sale_on_m3"));
                  break;
                default:
                  toast.error(t("toast_get_data_error"));
              }
            } else {
              toast.error(t("toast_get_data_error"));
            }
          });
      } else {
        setLoading(false);
        toast.error(t("toast_get_data_error"));
      }
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dealers.length || orderId) {
      initData();
    } else {
      setOrders([]);
      setFilteredOrders([]);
      setNoDataFound(true);
    }
  }, [dealers, orderId]);

  /*useEffect(() => {
    const hasActiveFilter = activeFilters.some(
      filter => filter.column === "status" && filter.value === "active/ouverte"
    );
    if (isOrdersToReceive && !hasActiveFilter) {
      setIsOrdersToReceive(false);
    }
  }, [activeFilters]);*/

  useEffect(() => {
    const hasActiveFilter = activeFilters.some(
      (filter) => filter.column === "status" && filter.value === "active/ouverte"
    );
    setIsOrdersToReceive(hasActiveFilter);
  }, [activeFilters]);

const removeFilter = (index: number) => {
  const newFilters = [...activeFilters];
  const removedFilter = newFilters.splice(index, 1)[0];

  // Check if the removed filter is status: active/ouverte
  if (
    removedFilter.column === "status" &&
    removedFilter.value === "active/ouverte"
  ) {
    setIsOrdersToReceive(false); // Update toggle state
  }

  setActiveFilters(newFilters);
};

  // Get selected column display name
  const getSelectedColumnLabel = () => {
    if (!selectedColumn) return t("filter_dropdown_list");
    const column = FILTER_COLUMNS.find((col) => col.value === selectedColumn);
    return column ? t(column.label) : t("filter_dropdown_list");
  };

  /** traduction des status */
    const statusOptions = [
      { label: t("status_open_active"), value: "active/ouverte" },
      { label: t("status_invoiced"), value: "facturée" },
    ];
/** traduction des status dans active filters */
    const statusLabels: Record<string, string> = {
      "active/ouverte": t("status_open_active"),
      "facturée": t("status_invoiced"),
    };
  return (
    <div className="orders">
      <PageTitle
        title={t("my_orders_title")}
        description={t("my_orders_subtitle")}
      />
      <div className="orders__main-wrapper">
        <div className="orders__actions-wrapper">
          <div className="orders__export">
            <div className="orders__export-button">{t("export")}</div>
            <div className="orders__export-options">
              <button className="orders__export-option" onClick={exportCsvData}>
                {t("csv_export")}
              </button>
              <button
                className="orders__export-option"
                onClick={exportExcelData}
              >
                {t("excel_export")}
              </button>
            </div>
          </div>
          <div className="orders__search-filter-container">
            <form onSubmit={handleSearch} className="orders__search-form">
                <div className="orders__toggle-wrapper">
                                <div className="orders__toggle-container">
                                  <span className="orders__undelivered-text">
                                                     {t("commandes_a_recevoir")}
                                  </span>
                                  <ToggleSwitch
                                    isOn={isOrdersToReceive}
                                    handleToggle={() => {
                                      // On vérifie si le filtre "active/ouverte" est déjà là
                                      const hasActiveFilter = activeFilters.some(
                                        (filter) =>
                                          filter.column === "status" && filter.value === "active/ouverte"
                                      );

                                      const newToggleState = !hasActiveFilter;

                                      if (newToggleState) {
                                        // CAS : On active le switch (ON)
                                        
                                        // 1. On retire TOUS les filtres de la colonne "status" (ex: facturée)
                                        const otherFilters = activeFilters.filter(
                                          (filter) => filter.column !== "status"
                                        );

                                        // 2. On ajoute le filtre "active/ouverte"
                                        setActiveFilters([
                                          ...otherFilters,
                                          { column: "status", value: "active/ouverte" },
                                        ]);
                                      } else {
                                        // CAS : On désactive le switch (OFF)
                                        setActiveFilters(
                                          activeFilters.filter(
                                            (filter) =>
                                              !(filter.column === "status" && filter.value === "active/ouverte")
                                          )
                                        );
                                      }

                                      setIsOrdersToReceive(newToggleState);
                                    }}
                                  />
                                </div>
                </div>
              <div className="orders__filter" ref={filterDropdownRef}>
                <div className="orders__filter-button">
                  {getSelectedColumnLabel()}
                </div>
                <div className="orders__filter-options">
                  {FILTER_COLUMNS.map((column) => (
                    <button
                      key={column.value}
                      className="orders__filter-option"
                      onClick={() => handleSelectFilterColumn(column.value)}
                    >
                      {t(column.label)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="orders__search-input-container">
                {(isInfoClientColumn || isProductionDateColumn) ? (
                  <input
                    type={selectedColumn === "productionDate" ? "date" : "text"}
                    className="orders__search-input"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyPress}
                    disabled={!selectedColumn}
                    placeholder={
                      !selectedColumn 
                        ? t("before_select_option")
                        : (selectedColumn === "productionDate" ? "YYYY-MM-DD" : t("search_placeholder"))
                    }
                  />
                ) : (
                  <Select
                    value={selectedOption}
                    options={selectedColumn === "status" ? statusOptions : filterOptions}
                    styles={customStyles}
                    onChange={handleOptionChange}
                    isDisabled={!selectedColumn}
                    placeholder={
                      !selectedColumn 
                        ? t("before_select_option")
                        : t("select_option")
                    }
                    isClearable
                    components={{ Input: CustomSelectInput }}
                    noOptionsMessage={() => t("pas_de_choix")}
                  />
                )}
              </div>

                <button
                  type="submit"
                  className="orders__search-button"
                  onClick={() => {
                    handleAddFilter();
                  }}
                >
                  {t("filter_button")}
                </button>

            </form>

            {activeFilters.length > 0 && (
              <div className="orders__active-filters">
                {activeFilters.map((filter, index) => (
                  <div key={index} className="orders__filter-tag">
                    <span className="orders__filter-text">
                      {filter.column
                        ? `${t(
                            FILTER_COLUMNS.find(
                              (col) => col.value === filter.column
                            )?.label || filter.column
                          )}: `
                        : ""}
                      {filter.column === "status"
                        ? statusLabels[filter.value]
                        : filter.column === "productionDate"
                          ? formatDate(filter.value)
                          : filter.value}
                    </span>
                    <button
                      className="orders__filter-remove"
                      onClick={() => removeFilter(index)}
                      aria-label={t("remove_filter")}
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {activeFilters.length > 1 && (
                  <button
                    className="orders__clear-filters"
                    onClick={clearAllFilters}
                  >
                    {t("remove_filters")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {noDataFound ? (
          <div className="orders__no-data">{t("data_not_found")}</div>
        ) : filteredOrders && filteredOrders.length ? (
          <OrderTable
            orders={sortByRGD2Date(filteredOrders)}
            userCanToggle={userCanToggle}
            setClientInfoModal={handleOpenClientInfoModal}
            setCommentaryModal={handleOpenCommentaryModal}
            onSelectedCUNOChange={handleSelectedCUNOChange}
            setPointOfSaleModal={handleOpenPointOfSaleModal}
          />
        ) : null}
        {isClientInfoModal && currentClient && (
          <Modal
            content={true}
            oncancel={() => SetIsClientInfoModal(false)}
            onValidate={() => SetIsClientInfoModal(false)}
          >
            <ClientInformations
              title={t("change_quote_info_client_order")}
              name="clientInfo"
              onClose={() => SetIsClientInfoModal(false)}
              client={currentClient}
              onClientChange={handleClientInfoChange}
            />
          </Modal>
        )}
        {isCommentaryModal && (
          <Modal
            content={true}
            oncancel={() => setIsCommentaryModal(false)}
            onValidate={() => setIsCommentaryModal(false)}
          >
            <CommentaryModal
              name="Commentary"
              onClose={() => setIsCommentaryModal(false)}
              onSelectedOptionChange={handleOrderCommentaryChange}
              commentary={currentCommentary}
            />
          </Modal>
        )}
        {isPointOfSaleModal && (
          <Modal
            content={true}
            oncancel={() => setIsPointOfSaleModal(false)}
            onValidate={() => setIsPointOfSaleModal(false)}
          >
            <PointOfSaleModalStock
              name="PointOfSale"
              onClose={() => setIsPointOfSaleModal(false)}
              selectedCUNO={selectedCUNO}
              onSelectedOptionChange={handleSelectedOptionChange}
            />
          </Modal>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;
